import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, withRouter } from 'react-router-dom'
import { Element } from 'react-scroll'
import { getPropertyInformation } from './actions/FlowAction'
import AppProperty from './AppProperty'
import AppVisitors from './AppVisitors'
import { Layout, PrivateRoute, Spinner } from './components'
import flowSelectors from './selectors/flowSelector'
import * as routeService from './services/routeService'
import * as translationService from './services/translationService'
import {
  ConfirmEmailSuccessView,
  ExternalReferenceView,
  ForgotPasswordView,
  LoginPasswordView,
  LoginView,
  NotFoundView,
  PrivacyView,
  ProfileView,
  RegisterView,
  ResetPasswordRequestedView,
  ForgotPasswordLinkSentView,
  ResetPasswordSuccessView,
  ResetPasswordView,
  VerificationView,
  RegisterEmailView,
  VerifyEmailView,
} from './views'
import PropertyNotFoundView from './views/PropertyNotFoundView'
import RegisterPasswordView from './views/RegisterPasswordView'
import { buildRedirectTarget } from './utils/domainRedirect'

class App extends Component {
  static propTypes = {
    getPropertyInformation: PropTypes.func,
    user: PropTypes.object,
  }

  state = {
    loading: true,
  }

  async componentDidMount() {
    const detectedLanguage = navigator.language
    translationService.changeLocale(detectedLanguage)
    document.title = translationService.siteTitle()

    const propertyId = routeService.getCurrentPropertyParam() || this.props.propertyInfoId

    if (propertyId) {
      const property = await this.props.getPropertyInformation(propertyId)
      // If this property's broker belongs to the other brand domain (RentMatch vs
      // Mijnhuurprofiel), bounce there BEFORE rendering the normal flow. Otherwise the app
      // briefly shows /login while the full-page redirect is still in flight. Keep the
      // spinner up (don't clear `loading`) so the normal flow never renders on this domain.
      if (this.maybeSwapDomain(property)) {
        return
      }
    }
    this.setState({ loading: false })
  }

  async componentDidUpdate(prevProps) {
    let propertyInfoId = null
    if (prevProps.propertyInfoId !== this.props.propertyInfoId) {
      propertyInfoId = this.props.propertyInfoId
    }
    if (propertyInfoId) {
      await this.props.getPropertyInformation(propertyInfoId)
    }

    // Safety net: if the broker for the current property loads/changes after mount, run the
    // same brand-domain swap. The primary path is componentDidMount, which swaps before the
    // normal flow renders.
    if (this.props.property !== prevProps.property) {
      this.maybeSwapDomain(this.props.property)
    }
  }

  // Redirect to the correct brand domain (rentmatch.be vs mijnhuurprofiel.be) based on the
  // broker's RENT_MATCH feature. Handles direct /property/:id links placed on broker sites.
  // Reads the broker off the loaded property (getPropertyInformation stores it under
  // state.property.broker; the top-level `broker` selector stays empty here). Returns true
  // if a full-page cross-domain redirect was initiated.
  maybeSwapDomain(property) {
    const broker = property && property.broker
    if (!broker) return false

    const propertyId = routeService.getCurrentPropertyParam()
    if (!propertyId) return false

    const features = Array.isArray(broker.features) ? broker.features : []
    const isRentMatchBroker = features.includes('RENT_MATCH')
    const isOnRentMatchDomain = window.location.hostname.includes('rentmatch')

    // Only ever swap mijnhuurprofiel -> rentmatch for RentMatch brokers.
    // The reverse (rentmatch -> mijnhuurprofiel) is intentionally NOT performed.
    if (isRentMatchBroker && !isOnRentMatchDomain) {
      const target = buildRedirectTarget(true, `/property/${propertyId}/rentmatch/intro`)
      if (target.type === 'absolute') {
        window.location.href = target.url
        return true
      }
    }
    return false
  }

  render() {
    const { loading } = this.state
    return loading ? (
      <Spinner size="large" />
    ) : (
      <Element name="mainContent">
        <Layout>
          <Switch>
            <Route exact path={'/404'}>
              <NotFoundView />
            </Route>
            <Route exact path={'/property-not-found'}>
              <PropertyNotFoundView />
            </Route>
            <Route exact path={['/login', '/property/:propertyId/login']} component={LoginView} />
            <Route
              exact
              path={['/passwordLogin', '/property/:propertyId/passwordLogin']}
              component={LoginPasswordView}
            />
            <Route
              exact
              path={['/registerPassword', '/property/:propertyId/registerPassword']}
              component={RegisterPasswordView}
            />
            <Route
              exact
              path={['/registerEmail', '/property/:propertyId/registerEmail']}
              component={RegisterEmailView}
            />
            <Route exact path={['/register', '/property/:propertyId/register']} component={RegisterView} />
            <Route exact path={['/verify-phone', '/property/:propertyId/verify-phone']} component={VerificationView} />
            <Route exact path={['/verify-email', '/property/:propertyId/verify-email']} component={VerifyEmailView} />
            <Route
              exact
              path={['/forgotPassword', '/property/:propertyId/forgotPassword']}
              component={ForgotPasswordView}
            />
            <Route exact path={['/success', '/property/:propertyId/success']} component={ResetPasswordSuccessView} />
            <Route
              exact
              path={['/resetRequested/sms', '/property/:propertyId/resetRequested/sms']}
              component={ResetPasswordRequestedView}
            />
            <Route
              exact
              path={['/resetRequested/email', '/property/:propertyId/resetRequested/email']}
              component={ForgotPasswordLinkSentView}
            />
            <Route exact path={['/privacy', '/property/:propertyId/privacy']} component={PrivacyView} />
            <Route
              exact
              path={['/emailConfirmation', '/property/:propertyId/emailConfirmation']}
              component={ConfirmEmailSuccessView}
            />
            <Route
              exact
              path={['/resetPassword/:resetToken', '/property/:propertyId/resetPassword/:resetToken']}
              component={ResetPasswordView}
            />
            <Route
              exact
              path={'/omnicasa/:reference'}
              render={props => <ExternalReferenceView {...props} type="omnicasa" />}
            />
            <Route
              exact
              path={'/mid/:reference'}
              render={props => <ExternalReferenceView {...props} type="maximmo" />}
            />
            <Route
              exact
              path={'/whise/:reference'}
              render={props => <ExternalReferenceView {...props} type="whise" />}
            />
            <Route
              exact
              path={'/sweepbright/:reference'}
              render={props => <ExternalReferenceView {...props} type="sweepbright" />}
            />
            <Route
              exact
              path={'/omnicasa/:reference'}
              render={props => <ExternalReferenceView {...props} type="omnicasa" />}
            />
            <Route
              exact
              path={'/extref/:reference'}
              render={props => <ExternalReferenceView {...props} type="extref" />}
            />
            <Route
              exact
              path={'/zabun/:reference'}
              render={props => <ExternalReferenceView {...props} type="zabun" />}
            />
            <Route
              exact
              path={'/skarabee/:reference'}
              render={props => <ExternalReferenceView {...props} type="skarabee" />}
            />
            <Route path={'/profiel'} render={props => <PrivateRoute {...props} component={ProfileView} />} />
            <Route path={'/property/:id'} render={props => <PrivateRoute {...props} component={AppProperty} />} />
            <Route path={'/'} render={props => <PrivateRoute {...props} component={AppVisitors} />} />

            <Route path="*" component={NotFoundView} />
          </Switch>
        </Layout>
      </Element>
    )
  }
}

const mapStateToProps = ({ AuthReducer, FlowReducer }) => {
  const { user = {} } = AuthReducer
  const { propertyInfoId } = FlowReducer

  return {
    user,
    broker: flowSelectors.broker(FlowReducer),
    property: flowSelectors.property(FlowReducer),
    propertyInfoId,
  }
}

const mapDispatchToProps = dispatch => ({
  getPropertyInformation: id => dispatch(getPropertyInformation(id)),
})

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(App),
)



// WEBPACK FOOTER //
// ./src/App.js